import type { FailureReason } from './types';

/**
 * WebRTC microphone capture (`getUserMedia`) is only exposed in a *secure
 * context*: HTTPS, or `http://localhost` / `127.0.0.1`. Served over a bare LAN
 * IP the mic is blocked, so registration can succeed while every call silently
 * fails (the far end sees NO_ANSWER). Returns a human explanation when blocked,
 * or `''` when media is available — surface this instead of a mystery failure.
 */
export function secureContextReason(): string {
  const ok =
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia;
  if (ok) return '';
  return (
    'Microphone blocked: this page is not a secure context. WebRTC needs HTTPS ' +
    '(or http://localhost). Serve your app over https:// — a plain http://<LAN-IP> ' +
    'origin cannot access the mic, so calls fail even though registration may succeed.'
  );
}

/** The bits of a JsSIP incoming SIP message we read a status out of. */
interface SipMessage {
  status_code?: number;
  reason_phrase?: string;
}

/**
 * Normalise a JsSIP end/fail/registrationFailed event into a stable shape.
 *
 * JsSIP does not put the SIP message in the same place on every event:
 * `registrationFailed` emits `{ response, cause }` (Registrator
 * `_registrationFailure`), while an RTCSession's `failed`/`ended` emit
 * `{ originator, message, cause }` — the final response lives in **`message`**,
 * and there is no `response` key at all. Reading only `response` therefore lost
 * the status on every call failure, so a refusal the platform had carefully
 * given a SIP code (402 insufficient balance, 503 traffic cap, 480 offline, …)
 * reached the UI as nothing but JsSIP's coarse cause word — and for any code
 * JsSIP has no name for (402, 5xx) that word is the useless `SIP Failure Code`.
 * Read both, preferring `response`.
 *
 * On `ended`, `message` is usually the BYE *request*, which carries no status —
 * the optional chaining leaves `statusCode` undefined and `cause` still explains
 * the hangup, which is exactly the old behaviour for that path.
 */
export function reasonOf(event: unknown): FailureReason {
  const e = event as { cause?: string; response?: SipMessage; message?: SipMessage } | undefined;
  const sip = e?.response ?? e?.message;
  return {
    cause: e?.cause,
    statusCode: sip?.status_code,
    reason: sip?.reason_phrase,
  };
}

/** One-line human summary of a failure, e.g. `"403 Forbidden · cause=Rejected"`. */
export function describeFailure(r: FailureReason): string {
  const sip = r.statusCode && r.reason ? `${r.statusCode} ${r.reason}` : '';
  const cause = r.cause ? `cause=${r.cause}` : '';
  return [sip, cause].filter(Boolean).join(' · ') || 'unknown';
}
